import type { AboutChapter as AboutChapterData } from "./data";
import { ChapterSection } from "../../components/ChapterSection/index";
import { TypewriterText } from "./TypewriterText";
import { ContactForm } from "../../components/ContactForm/index";

type AboutChapterProps = {
  chapter: AboutChapterData;
  index: number;
  isActive: boolean;
  isVisible: boolean;
  isPageReady: boolean;
  isContactMode: boolean;
  isLastChapter: boolean;
  contactExpanded: boolean;
  typewriterDone: boolean;
  reducedMotion: boolean;
  hasScrolled: boolean;
  showCursor: boolean;
  headlineText: string;
  subheadText: string;
  headlineAnimating: boolean;
  subheadAnimating: boolean;
  linksReady: boolean;
  linksVisible: boolean;
  typingDone: boolean;
  onExpandContact?: () => void;
  onTypewriterDone: (part: "headline" | "subhead") => void;
};

export const AboutChapter = ({
  chapter,
  index,
  isActive,
  isVisible,
  isPageReady,
  isContactMode,
  isLastChapter,
  contactExpanded,
  typewriterDone,
  reducedMotion,
  hasScrolled,
  showCursor,
  headlineText,
  subheadText,
  headlineAnimating,
  subheadAnimating,
  linksReady,
  linksVisible,
  typingDone,
  onExpandContact,
  onTypewriterDone,
}: AboutChapterProps) => {
  const isContact = chapter.id === "contact";

  // Non-contact chapters use the shared ChapterSection
  if (!isContact) {
    return (
      <ChapterSection
        chapter={chapter}
        index={index}
        isActive={isActive}
        isVisible={isVisible}
        isPageReady={isPageReady}
        isFirstChapter={index === 0 && !isContactMode}
        isLastChapter={false}
        typewriterDone={typewriterDone}
        reducedMotion={reducedMotion}
        hasScrolled={hasScrolled}
        onTypewriterDone={onTypewriterDone}
      />
    );
  }

  // Contact chapter keeps its special logic
  const expanded = contactExpanded;

  return (
    <section
      id={chapter.id}
      className={`scroll-chapter${isActive ? " is-active" : ""}${isVisible ? " is-visible" : ""}${expanded ? " is-contact-expanded" : ""}${typewriterDone ? " is-typewriter-done" : ""}`}
      data-chapter-id={chapter.id}
      data-chapter-label={chapter.label}
      aria-labelledby={`${chapter.id}-headline`}
    >
      <div className="about-chapter__content">
        <div className="about-chapter__left">
          <span className="about-chapter__mark" aria-hidden="true">
            <span className="about-chapter__mark-dot" />
            <span className="about-chapter__mark-line" />
          </span>
          <p className="about-chapter__eyebrow">{chapter.label}</p>
          {!isContactMode && !expanded ? (
            <TypewriterText
              variant="headline"
              id={`${chapter.id}-headline`}
              text={chapter.headline}
              active={isVisible && isPageReady}
              reducedMotion={reducedMotion}
              onDone={() => onTypewriterDone("headline")}
            />
          ) : (
            <h2
              id={`${chapter.id}-headline`}
              className="about-chapter__headline"
            >
              {headlineText.split("\n").map((line, i, arr) => (
                <span key={i}>
                  {line}
                  {i < arr.length - 1 ? <br /> : null}
                </span>
              ))}
              {showCursor && headlineAnimating && (
                <span
                  className="about-chapter__cursor"
                  aria-hidden="true"
                />
              )}
            </h2>
          )}
          {!isContactMode && !expanded ? (
            <TypewriterText
              variant="subhead"
              text={chapter.subhead}
              active={isVisible && isPageReady}
              reducedMotion={reducedMotion}
              onDone={() => onTypewriterDone("subhead")}
            />
          ) : (
            <p className="about-chapter__subhead">
              {subheadText}
              {showCursor && subheadAnimating && !headlineAnimating && (
                <span className="about-chapter__cursor" aria-hidden="true" />
              )}
            </p>
          )}
          {linksReady && (
            <div
              className={`about-chapter__contact-links${linksVisible ? " is-visible" : ""}`}
              aria-label="Contact endpoints"
            >
              <a
                className="about-chapter__contact-link"
                href="mailto:hello@ikaniewski.dev"
                aria-label="Send email to hello@ikaniewski.dev"
              >
                <span
                  className="about-chapter__contact-link-icon"
                  aria-hidden="true"
                >
                  <svg
                    width="14"
                    height="11"
                    viewBox="0 0 14 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 2.5L6.4 6.1a1 1 0 0 0 1.2 0L13 2.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect
                      x="1"
                      y="1"
                      width="12"
                      height="9"
                      rx="1.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </span>
                <span>hello@ikaniewski.dev</span>
              </a>
              <a
                className="about-chapter__contact-link"
                href="https://www.linkedin.com/in/ikaniewski-dev/"
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Open LinkedIn profile"
              >
                <span
                  className="about-chapter__contact-link-icon"
                  aria-hidden="true"
                >
                  in
                </span>
                <span>linkedin.com/in/ikaniewski-dev/</span>
              </a>
            </div>
          )}
        </div>
        <div className="about-chapter__right">
          <ContactForm
            className={`about-chapter__contact-wrap${expanded ? " is-expanded" : ""}${expanded && typingDone ? " is-typing-done" : ""}`}
            fieldsReady={expanded && typingDone}
            enableTabIndex={expanded}
            showButton={!isContactMode || typingDone || !expanded}
            buttonText={
              expanded && typingDone ? "SEND MESSAGE" : "LET'S GET IN TOUCH"
            }
            buttonType={expanded ? "submit" : "button"}
            onButtonClick={expanded ? undefined : onExpandContact}
            buttonClassName="about-chapter__contact-action"
            buttonFading={expanded && !typingDone}
          />
        </div>
      </div>
      {isLastChapter ? (
        <footer className="about-chapter__footer" aria-label="Copyright">
          &copy; 2026 Igor Kaniewski &middot; All rights reserved.
        </footer>
      ) : null}
    </section>
  );
};
